// THE EXTEND STEP — giving a running poll more time. The Administrator's one act on a
// poll that is already open, and the whole user interface for
// `POST /api/setup/ballot/:ballotId/extend`.
//
// ── IT IS NOT A POINT OF NO RETURN, AND IT IS NOT DRESSED AS ONE ───────────
// Opening a ballot starts a binding vote and cannot be undone; extending one ENLARGES
// the window and can be done again. So this step is deliberately lighter than
// {@link OpenStep}: ONE confirm, no danger colour, no type-the-title, no second tap
// after the first. Over-fencing an enfranchising act has a cost that is easy to miss —
// a registrar in a hall who cannot get past a screen leaves the poll shut, and members
// who were still walking to the door do not vote.
//
// ── THE CONFIRM IS THE ABANDON SHAPE, NOT THE OPEN SHAPE ───────────────────
// The time field lives INSIDE `ConfirmBlock`'s children and `confirmDisabled` gates
// the confirm on it — the same arrangement the abandon reason takes in
// `BallotsPanel`. That is what makes it one press: the operator arrives here from the
// panel's ghost, types a time, and presses once. An intermediate submit button would
// be a second confirm for an act that does not warrant one.
//
// ── FOCUS LANDS ON THE HEADING, AND THE ORDER IS NOT LUCK ──────────────────
// `ConfirmBlock` focuses its own prompt on mount and this component focuses its `h1`
// on mount, and both mount in the same commit. React flushes effects depth-FIRST —
// children before parents — so the child's focus call runs first and this component's
// runs last, which is the one that sticks. That is the guarantee being relied on, and
// it is written down because it is invisible in the code: the operator lands on the
// title of the poll they are about to change, not on a confirm prompt about it.
//
// ── ENTER IN THE FIELD MUST NOT SEND ANYTHING ──────────────────────────────
// A form with a single text input submits on Enter whether or not it has a submit
// button, so the field is wrapped in a `<form>` whose `onSubmit` does nothing but
// prevent the default — the same fence `OpenStep` keeps. The send is a deliberate
// press on a button that names the time it is sending, never a stray Return in a
// datetime field.
//
// ── A REFUSAL DOES NOT COST THE OPERATOR THE SCREEN ────────────────────────
// On failure the parent stays on this view (`SetupPage`'s `doExtend`, the precedent
// `doOpen` set) and hands the code and the refusal's own figures back down, so the
// operator corrects the number in front of the sentence explaining it rather than
// being returned to the hub to find their way back. THE FIGURES ARE THE POINT: the
// route sends the close that stands, the latest instant it would accept and the cap in
// hours precisely so a registrar is never told "no" without being told what to type.
//
// ── AND SUCCESS IS REPORTED FROM THE SERVER'S ANSWER ───────────────────────
// The parent announces the WORKER's `title` and `closesAtNz`, never what this screen
// believed it pressed. Extending the wrong ballot is silent and cannot be undone, and
// the two idempotent 200s can report a window somebody else set — so what is read back
// to the operator is the row, not the request.

import { useRef, useState, type JSX } from 'react';
import { useFocusOnMount } from '../a11y/useFocusOnMount';
import { ConfirmBlock } from './ConfirmBlock';
import { STATE_LABEL } from './BallotsPanel';
import { refusalSentence } from './copy';
import { NZ_WALL_CLOCK, RecordedBound, utcInWords, wallClockInWords } from './SchedulePanel';
import type { DraftBallot, RefusalDetails } from '../api/client';

/** A refusal detail as a string, or `null` — the narrowing every read of
 *  {@link RefusalDetails} owes, in one place. The values are `unknown` by design: the
 *  client keeps the refusal body verbatim and knows nothing about which figures a
 *  given code carries, so the screen that does know narrows before it renders. */
function text(details: RefusalDetails, key: string): string | null {
  const value = details[key];
  return typeof value === 'string' && value !== '' ? value : null;
}

/** The one numeric detail — `maxExtensionHours`, derived server-side from
 *  `MAX_EXTENSION_MS` so the limit an operator is told is the limit enforced. Finite
 *  only: an infinity or a NaN off the wire renders nothing rather than "Infinity". */
function count(details: RefusalDetails, key: string): number | null {
  const value = details[key];
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

/**
 * A refusal as a sentence WITH THE FIGURE THAT BEATS IT IN FRONT OF IT.
 *
 * The words are `copy.ts`'s (`SETUP_REFUSAL_COPY` carries all seven extend codes); the
 * figures are this screen's to render, because turning an instant into "4:00pm on 19
 * September 2026" needs `wallClockInWords`, and `copy.ts` is deliberately free of
 * runtime imports so its test stays hermetic. This is the same split `SchedulePanel`'s
 * `scheduleRefusal` keeps one file over.
 *
 * EVERY ARM FALLS BACK TO THE BARE SENTENCE when its figure did not arrive — an older
 * worker, a body that did not parse, a refusal raised before the row was read. A
 * missing figure must never produce a half-written sentence at the moment somebody is
 * trying to keep a poll open.
 */
function extendRefusal(code: string, details: RefusalDetails): string {
  const base = refusalSentence(code);
  switch (code) {
    case 'not_an_extension': {
      // The close that STANDS. Without it the operator does not know what they have to
      // beat and can only guess a second time — which is the whole reason the route
      // sends it. `closesAtNz` is null on the one arm where the stored close could not
      // be read at all, and then there is honestly no figure to give.
      const nz = text(details, 'closesAtNz');
      if (nz === null) return base;
      return `This poll already closes at ${wallClockInWords(nz)}, New Zealand time. ${base}`;
    }
    case 'extension_too_far': {
      const latest = text(details, 'latestAcceptableNz');
      const hours = count(details, 'maxExtensionHours');
      if (latest === null) return base;
      const cap =
        hours === null
          ? 'One extension may only carry a poll so far'
          : `One extension may move a poll at most ${String(hours)} hours`;
      return `${cap}, so the latest you can set from here is ${wallClockInWords(latest)}, New Zealand time. ${base}`;
    }
    case 'extend_invalid': {
      // The route's OWN words for the shape it wanted, and its own example — not a
      // second description written here that could drift from the parser.
      //
      // **`schedule_time_does_not_exist` IS DELIBERATELY NOT IN THIS ARM**, even though
      // the route sends `expected` and `example` with it too (they are spread into both
      // bad-body refusals). There the STRING was well formed and the CLOCK TIME was
      // not — the hour New Zealand skips entering daylight saving — so telling the
      // operator the format would send them to correct the one thing that was already
      // right. Its own sentence, in `copy.ts`, names the missing hour instead, which is
      // the repair. Sharing an arm because the two bodies share a field would have been
      // exactly the collapse the worker spends a separate word to prevent.
      const expected = text(details, 'expected');
      const example = text(details, 'example');
      if (expected === null || example === null) return base;
      return `${base} Give it as ${expected} — for example ${example}.`;
    }
    case 'not_an_open_poll': {
      // WHICH state, in the operator's word rather than the wire's. `state` is the one
      // detail the client already lifts out separately, and it arrives here too.
      const state = text(details, 'state');
      if (state === null) return base;
      return `${base} This ballot is “${STATE_LABEL[state] ?? state}”.`;
    }
    default:
      return base;
  }
}

interface Props {
  ballot: DraftBallot;
  busy: boolean;
  /** The wire code of the last extend refusal, or `null`. */
  error: string | null;
  /** That refusal's own body — the figures its repair needs. Empty when there has
   *  been no refusal, so nothing here needs a null check in front of a lookup. */
  errorDetails: RefusalDetails;
  /** The new closing time as a zoneless NZ wall clock, `YYYY-MM-DDTHH:MM`, exactly as
   *  the field holds it. **This browser never derives the instant** — the worker does,
   *  from the zone for the date given. */
  onExtend: (closesAtNz: string) => void;
  onCancel: () => void;
}

export function ExtendStep({
  ballot,
  busy,
  error,
  errorDetails,
  onExtend,
  onCancel,
}: Props): JSX.Element {
  // EMPTY, never seeded from the standing close. A field pre-filled with the time that
  // is already recorded invites a press that moves nothing — the route would answer
  // 200 as an idempotent retry and the operator would believe the poll had been
  // extended. The new time is typed, deliberately, the same way the quorum is declared.
  const [closesRaw, setClosesRaw] = useState('');

  // Focus the heading on entry — see the header for why this wins over the confirm
  // prompt's own mount focus.
  const heading = useRef<HTMLHeadingElement>(null);
  useFocusOnMount(heading);

  const asked = closesRaw.trim();
  // The SAME shape test the schedule field keeps, from the same exported pattern: a
  // sixteen-character wall clock and nothing else. It is a mirror of the route's
  // parser and never a substitute for it — the worker re-reads the string, decides
  // which instant it names, and refuses an hour that does not exist on that date,
  // which is timezone arithmetic this file is forbidden (and does not attempt).
  const valid = NZ_WALL_CLOCK.test(asked);

  const closesNz = ballot.schedule?.closesAtNz ?? null;
  const closesUtc = ballot.schedule?.closesAtUtc ?? null;

  const fieldId = `extend-${ballot.id}`;
  const hintId = `${fieldId}-hint`;

  return (
    <section className="setup-extend-step" aria-labelledby="setup-extend-heading">
      <button type="button" className="btn btn-ghost setup-back" onClick={onCancel} disabled={busy}>
        Back to setup
      </button>

      <h1 className="ballot-title" id="setup-extend-heading" tabIndex={-1} ref={heading}>
        Give “{ballot.title}” more time
      </h1>
      <p className="setup-lede">
        This moves the poll’s closing time later and does nothing else. Members who have already
        marked a paper keep it; members who have not can still vote, right up to the new time.
        Nobody loses time by this — a poll can only ever be given more.
      </p>

      {/* THE WINDOW THAT STANDS, BEFORE THE FIELD. The operator is being asked for a
          time later than this one, so it has to be on the screen at the moment they
          type — the derived instant beneath it, in the muted role, being the only
          place the applied daylight-saving offset is visible. */}
      <RecordedBound
        label="Closes"
        nz={closesNz}
        utc={closesUtc}
        unbounded={
          closesUtc === null
            ? 'not on a clock — this poll stays open until a Scrutineer closes it.'
            : `not recorded as a New Zealand clock time. The poll closes at ${utcInWords(closesUtc)}.`
        }
      />

      {error !== null ? (
        <p className="alert" role="alert">
          {extendRefusal(error, errorDetails)}
        </p>
      ) : null}

      {/* A REAL FORM whose only job is to swallow Enter — there is no submit button in
          it, and `ConfirmBlock`'s two buttons are both `type="button"`. */}
      <form
        className="setup-extend-form"
        onSubmit={(event) => {
          event.preventDefault();
        }}
      >
        <ConfirmBlock
          prompt={`Move the closing time on “${ballot.title}”?`}
          // THE LABEL CARRIES THE TIME. A bare "Yes, extend" would let a mistyped date
          // through on a press that named nothing; this states, in words, the instant
          // about to be sent, so the last thing read before the press is the figure.
          confirmLabel={
            valid ? `Yes, close at ${wallClockInWords(asked)}` : 'Yes, move the closing time'
          }
          busy={busy}
          confirmDisabled={!valid}
          onConfirm={() => {
            // `confirmDisabled` already gates the button; this is the narrowing, and
            // if it were ever reached with a bad value nothing is sent.
            if (valid) onExtend(asked);
          }}
          onCancel={onCancel}
        >
          <div className="setup-field">
            <label htmlFor={fieldId}>New closing time (New Zealand time)</label>
            <input
              id={fieldId}
              className="setup-input setup-extend-input"
              type="datetime-local"
              step={60}
              value={closesRaw}
              disabled={busy}
              aria-describedby={hintId}
              onChange={(event) => setClosesRaw(event.target.value)}
            />
            {/* NO NUMBER OF HOURS IS NAMED HERE, DELIBERATELY. The cap is
                `MAX_EXTENSION_MS` in the worker and this browser cannot read it; a
                literal "twelve hours" typed into this hint would go on saying twelve
                the day that constant changes, and an operator told a limit that is not
                the one enforced is worse off than one told none. The real figure
                arrives on the refusal, derived from the constant, and is rendered
                there. A poll may also be extended more than once, so the cap is on the
                move rather than on how long a sitting may run. */}
            <span className="setup-field-hint" id={hintId}>
              The clock in the hall. It must be later than the time above, and one move can only
              carry a poll so far — if it is too far, the screen will say the latest time it will
              take, and you can move it again after that.
            </span>
          </div>
        </ConfirmBlock>
      </form>
    </section>
  );
}
